import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Copy, Check } from 'lucide-react';
import { getCycles, getSummary, getClassGroups, postApplication, errText } from '@/api/admissions';
import { useAuth } from '@/stores/auth';
import { Stat, Btn, Modal, Field, input, Notice } from '@/features/owner/admissions/ui';
import { ApplicationsTab } from '@/features/owner/admissions/ApplicationsTab';
import { PaymentsTab } from '@/features/owner/admissions/PaymentsTab';
import { ExamsTab } from '@/features/owner/admissions/ExamsTab';
import { AdmitTab } from '@/features/owner/admissions/AdmitTab';
import { SettingsTab } from '@/features/owner/admissions/SettingsTab';
import { webUrl } from '@/lib/native';

type Tab = 'applications' | 'payments' | 'exams' | 'admit' | 'settings';

export function AdmissionsPage() {
  const user = useAuth((s) => s.user);
  const [tab, setTab] = useState<Tab>('applications');
  const [cycleId, setCycleId] = useState<number | ''>('');
  const [walkIn, setWalkIn] = useState(false);
  const [copied, setCopied] = useState(false);

  const cyclesQ = useQuery({ queryKey: ['adm-cycles'], queryFn: getCycles });
  const cycles = cyclesQ.data ?? [];
  const groupsQ = useQuery({ queryKey: ['adm-groups'], queryFn: getClassGroups });
  const groups = groupsQ.data ?? [];
  useEffect(() => { if (!cycleId && cycles.length) setCycleId((cycles.find((c) => c.is_open) ?? cycles[0]).id); }, [cycles, cycleId]);
  const cycle = cycles.find((c) => c.id === cycleId);
  const sumQ = useQuery({ queryKey: ['adm-summary', cycleId], queryFn: () => getSummary(cycleId), enabled: cycles.length > 0 });
  const s: any = sumQ.data ?? {};

  const applyLink = `${webUrl()}/apply${user?.schoolCode ? `?school=${encodeURIComponent(user.schoolCode)}` : ''}`;
  const tabs: { key: Tab; label: string; badge?: number }[] = [
    { key: 'applications', label: 'Applications', badge: s.total },
    { key: 'payments', label: 'Payments', badge: s.receipts_to_verify },
    { key: 'exams', label: 'Entrance exams' },
    { key: 'admit', label: 'Bulk admit' },
    { key: 'settings', label: 'Settings' },
  ];

  return (
    <div className="max-w-[1400px] mx-auto p-4 sm:p-6 space-y-5 pb-24">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">School</p>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">Admissions</h1>
          <p className="text-slate-500 text-sm mt-1">From application and payment to entrance exam and admission as a student.</p>
        </div>
        <div className="grid grid-cols-1 sm:flex gap-2">
          {cycles.length > 0 && (
            <select value={cycleId} onChange={(e) => setCycleId(Number(e.target.value))} className={`${input} sm:w-56`} aria-label="Admission cycle">
              {cycles.map((c) => <option key={c.id} value={c.id}>{c.name}{c.is_open ? '' : ' (closed)'}</option>)}
            </select>
          )}
          <Btn kind="ghost" onClick={() => { navigator.clipboard?.writeText(applyLink); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>{copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}{copied ? 'Copied' : 'Copy apply link'}</Btn>
          <Btn kind="dark" disabled={!cycle} onClick={() => setWalkIn(true)}><Plus className="w-4 h-4" />Walk-in application</Btn>
        </div>
      </div>

      {cycles.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Stat label="Applications" value={s.total ?? 0} sub={`+${s.this_week ?? 0} this week`} />
          <Stat label="Awaiting payment" value={s.awaiting_payment ?? 0} tone={(s.awaiting_payment ?? 0) > 0 ? 'amber' : undefined} />
          <Stat label="Receipts to verify" value={s.receipts_to_verify ?? 0} tone={(s.receipts_to_verify ?? 0) > 0 ? 'amber' : undefined} />
          <Stat label="Exam taken" value={s.exam_taken ?? 0} sub="awaiting decision" />
          <Stat label="Admitted" value={s.enrolled ?? 0} tone="green" />
        </div>
      )}

      <div className="border-b border-slate-200 -mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto">
        <div className="flex gap-5 min-w-max">
          {tabs.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} className={`py-3 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1.5 ${tab === t.key ? 'border-[#0f172a] text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
              {t.label}{!!t.badge && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${t.key === 'payments' ? 'bg-amber-500 text-white' : 'bg-slate-200 text-slate-700'}`}>{t.badge}</span>}
            </button>
          ))}
        </div>
      </div>

      {cyclesQ.isLoading ? <p className="text-sm text-slate-500">Loading…</p> : cycles.length === 0 && tab !== 'settings' ? (
        <Notice tone="info">No admission cycle yet. <button className="underline font-semibold" onClick={() => setTab('settings')}>Create one in Settings</button> to start taking applications.</Notice>
      ) : (
        <>
          {tab === 'applications' && <ApplicationsTab cycleId={cycleId} groups={groups} />}
          {tab === 'payments' && <PaymentsTab />}
          {tab === 'exams' && <ExamsTab cycle={cycle} groups={groups} />}
          {tab === 'admit' && <AdmitTab cycle={cycle} groups={groups} />}
          {tab === 'settings' && <SettingsTab cycle={cycle} cycles={cycles} onCycleCreated={(id) => setCycleId(id)} />}
        </>
      )}

      {walkIn && cycle && <WalkInModal cycleId={cycle.id} groups={groups} onClose={() => setWalkIn(false)} />}
    </div>
  );
}

function WalkInModal({ cycleId, groups, onClose }: { cycleId: number; groups: { id: number; name: string; key: string }[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: (b: any) => postApplication(b),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['adm-apps'] }); qc.invalidateQueries({ queryKey: ['adm-summary'] }); onClose(); },
    onError: (e) => setErr(errText(e)),
  });
  return (
    <Modal title="Walk-in application" onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); const f = Object.fromEntries(new FormData(e.currentTarget)); m.mutate({ ...f, admission_cycle_id: cycleId, class_applied_id: Number(f.class_applied_id) }); }} className="space-y-3">
        <div className="grid grid-cols-2 gap-3"><Field label="First name"><input name="first_name" required className={input} /></Field><Field label="Last name"><input name="last_name" required className={input} /></Field></div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Gender"><select name="gender" required className={input}><option value="female">Female</option><option value="male">Male</option></select></Field>
          <Field label="Date of birth"><input name="date_of_birth" type="date" className={input} /></Field>
        </div>
        <Field label="Class applying for"><select name="class_applied_id" required className={input}>{groups.map((g) => <option key={g.key} value={g.id}>{g.name}</option>)}</select></Field>
        <Field label="Parent / guardian name"><input name="guardian_name" required className={input} /></Field>
        <Field label="Parent phone"><input name="guardian_phone" required className={input} inputMode="tel" /></Field>
        <Field label="Previous school (optional)"><input name="previous_school" className={input} /></Field>
        {err && <Notice tone="err">{err}</Notice>}
        <Btn type="submit" disabled={m.isPending} className="w-full">Save application</Btn>
      </form>
    </Modal>
  );
}
